import React from "react";
import { Route, Switch } from "react-router-dom";

import * as QueriesQL from "queries/point";
import PointPublicSchema from "@common/schema/point/public";
import PointLimitPublicSchema from "@common/schema/point-limit/public";
import * as Section from "@repoflow/components/section";
import { Crumb, useCrumbLabel } from "@repoflow/components/breadcrumb";

import * as ViewRoute from "./view";
import * as EditRoute from "./edit";
import * as RemoveRoute from "./remove";
import * as LimitsRoute from "./limits";
import * as CapturesRoute from "./captures";

const LocalProvider = ({
  match: {
    params,
    params: { pointId },
  },
  children: Children,
}) => {
  const { base: parentBase, basepath: parentBasepath } =
    Section.useSectionContext();

  const base = `${parentBase}/${pointId}`;
  const basepath = `${parentBasepath}/:pointId`;

  // Keyed on the entity, which is what the accidental remount used to do.
  //
  // Every screen below seeds its form state ONCE, from the entity this
  // route resolved. Until the render prop above was called rather than
  // rendered, React saw a new component type on every render and remounted
  // the lot — which reset those forms, and happened to be what made moving
  // from one pointId to another show the new one's values. It no longer
  // remounts for free, so the reset it was providing is asked for here.
  return (
    <Section.Provider
      key={pointId}
      // Two schemas, because everything under this point resolves its widgets
      // out of one section context: the point on the view screen and its limits
      // under /limit. The child sections inherit this list
      // (`schema={[...schema]}`), so a schema a child listing needs has to be
      // here rather than there.
      //
      // It was four until 2026-08-13. The capture and point-usage schemas went
      // with the two listings that drew them — the row-level capture explorer
      // and the usage table, both under /capture — and that section draws charts
      // now, which read the aggregate directly and resolve no widgets at all.
      schema={[...PointPublicSchema, ...PointLimitPublicSchema]}
      base={base}
      basepath={basepath}
      attributes={params}
    >
      {/* Called, never rendered as an element — see Section.Provider. */}
      {Children({ base, basepath, params })}
    </Section.Provider>
  );
};

export function Content(props) {
  return (
    <LocalProvider {...props}>
      {({ params }) => (
        <Section.Query
          query={QueriesQL.get}
          variables={params}
          datapath={"points.get"}
        >
          {ContentHandler}
        </Section.Query>
      )}
    </LocalProvider>
  );
}

function ContentHandler({ basepath }) {
  return (
    <Switch>
      
      <Route path={`${basepath}/limit`} component={LimitsRoute.Content} />
      <Route path={`${basepath}/capture`} component={CapturesRoute.Content} />
      <Route path={`${basepath}/remove`} component={RemoveRoute.Content} />
      <Route path={`${basepath}/edit`} component={EditRoute.Content} />
      <Route path={`${basepath}`} component={ViewRoute.Content} />
    </Switch>
  );
}

// The id is generated and says nothing; the description is what the point is
// called everywhere else on the page.
function PointCrumb({ base, pointId }) {
  const label = useCrumbLabel({
    query: QueriesQL.get,
    variables: { pointId },
    datapath: "points.get.description",
    fallback: pointId,
  });

  return <Crumb to={`${base}`} label={label} />;
}

export function Indice(props) {
  return (
    <LocalProvider {...props}>
      {({ base, basepath, params: { pointId } }) => (
        <>
          <PointCrumb base={base} pointId={pointId} />

          <Switch>
          <Route path={`${basepath}/limit`} component={LimitsRoute.Indice} />
            <Route path={`${basepath}/capture`} component={CapturesRoute.Indice} />
            <Route path={`${basepath}/remove`} component={RemoveRoute.Indice} />
            <Route path={`${basepath}/edit`} component={EditRoute.Indice} />
            <Route path={`${basepath}`} component={ViewRoute.Indice} />
          </Switch>
        </>
      )}
    </LocalProvider>
  );
}

export const Sidebar = () => (
  <>
    <EditRoute.Sidebar />
    <ViewRoute.Sidebar />
    <LimitsRoute.Sidebar />
    <CapturesRoute.Sidebar />
  </>
);
